import { useReduxState } from 'hooks';
import { EmptyIndicator, MoreOptions } from 'components/common';
import { Box, BoxOverflowWrapper } from 'components/common/box';
import {
  CalendarEventFeedbackQuestionRemove,
  CalendarEventFeedbackQuestionsOrder,
  CalendarEventFeedbackQuestionSave,
  CalendarEventFeedbackQuestionSummary,
  CalendarEventFeedbackTemplateApply
} from '.';

import type { CalendarEventFeedbackQuestion } from 'shared-types';
import type { GetCalendarEventFeedbackAnalyticsRes } from 'shared-types/responses';

interface Props {
  analyticsByQuestionId?: GetCalendarEventFeedbackAnalyticsRes['analyticsByQuestionId'];
  applyTemplateUrl?: string;
  getQuestionHref?: (question: CalendarEventFeedbackQuestion) => string;
  hasSubmissions: boolean;
  isDefaultTemplate?: boolean;
  isTemplate?: boolean;
  loading: boolean;
  questions: CalendarEventFeedbackQuestion[];
  questionsOrderUrl: string;
  questionsUrl: string;
  updateQuestions: () => Promise<void>;
}

const CalendarEventFeedbackQuestions = ({
  analyticsByQuestionId,
  applyTemplateUrl,
  getQuestionHref,
  hasSubmissions,
  isDefaultTemplate = false,
  isTemplate = false,
  loading,
  questions,
  questionsOrderUrl,
  questionsUrl,
  updateQuestions
}: Props) => {
  const { messages, theme } = useReduxState();

  const removeAllDisabled =
    hasSubmissions || !questions.length || isDefaultTemplate;

  const removeAllTooltip = hasSubmissions
    ? messages.eventCalendarFeedbackQuestionsLockedTooltip
    : isDefaultTemplate
    ? messages.siteSettingsCalendarEventFeedbackQuestionsDefaultTemplateTooltip
    : undefined;

  return (
    <div className="questions">
      <Box
        headerContent={
          <div style={{ alignItems: 'center', display: 'flex', gap: 10 }}>
            <CalendarEventFeedbackQuestionSave
              disabled={hasSubmissions}
              questionsUrl={questionsUrl}
              updateQuestions={updateQuestions}
            />

            {applyTemplateUrl ? (
              <CalendarEventFeedbackTemplateApply
                applyTemplateUrl={applyTemplateUrl}
                disabled={hasSubmissions}
                hasQuestions={Boolean(questions.length)}
                tooltip={messages.eventCalendarFeedbackQuestionsLockedTooltip}
                updateQuestions={updateQuestions}
              />
            ) : null}

            <MoreOptions category="lighter">
              <CalendarEventFeedbackQuestionsOrder
                disabled={hasSubmissions}
                questionsOrderUrl={questionsOrderUrl}
                questions={questions}
                updateQuestions={updateQuestions}
              />

              <CalendarEventFeedbackQuestionRemove
                disabled={removeAllDisabled}
                questionsUrl={questionsUrl}
                tooltip={removeAllTooltip}
                updateQuestions={updateQuestions}
              />
            </MoreOptions>
          </div>
        }
        loading={loading}
        padding={0}
        title={messages.commonQuestions}
      >
        {questions.length ? (
          <BoxOverflowWrapper>
            {questions.map((question, questionIndex) => (
              <div className="question" key={question.id}>
                <div className="question-summary">
                  <CalendarEventFeedbackQuestionSummary
                    analytics={analyticsByQuestionId?.[question.id]}
                    getQuestionHref={getQuestionHref}
                    hasSubmissions={hasSubmissions}
                    isDefaultTemplate={isDefaultTemplate}
                    isTemplate={isTemplate}
                    number={questionIndex + 1}
                    question={question}
                    questions={questions}
                    questionsUrl={questionsUrl}
                    updateQuestions={updateQuestions}
                  />
                </div>
              </div>
            ))}
          </BoxOverflowWrapper>
        ) : (
          <EmptyIndicator
            icon="question-circle"
            title={messages.eventCalendarFeedbackQuestionsEmpty}
          />
        )}
      </Box>

      <style jsx>{`
        .question {
          display: flex;
          gap: 20px;
          padding: 20px 15px;
        }
        .question:not(:first-child) {
          border-top: 1px solid ${theme.lightGreyDark};
        }
        .question-summary {
          flex-grow: 1;
          flex-shrink: 1;
        }
        .question-actions {
          flex-grow: 0;
          flex-shrink: 0;
        }
      `}</style>
    </div>
  );
};

export default CalendarEventFeedbackQuestions;
